import { useState, ReactNode, memo, useMemo, useCallback, useEffect, useRef, RefObject } from 'react';
import clsx from 'clsx';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';

import {
  GlobalModalContext,
  ModalConfig,
} from '../Contexts/GlobalModalContext';
import { RouteAreaContext } from '../../utilities/helpers/routeArea';
import {
  resolveModalShortcut,
  shortcutSelector,
  toModalFocusInfo,
  toModalKeyEvent,
} from '../Modals/modalShortcuts';

export const GlobalModalProvider = ({ children }: { children: ReactNode }) => {
  const location = useLocation();
  const [isVisible, setIsVisible] = useState(false);
  const [modalConfig, setModalConfig] = useState<ModalConfig | null>(null);
  const [modalKey, setModalKey] = useState<string | undefined>(undefined);
  const [prevLocationKey, setPrevLocationKey] = useState(location.key);
  const panelRef = useRef<HTMLDivElement | null>(null);

  const showModal = useCallback(
    (config: ModalConfig, key?: string) => {
      setModalConfig(() => config);
      setModalKey(key);
      setIsVisible(true);
    },
    []
  );

  const hideModal = useCallback(() => {
    setIsVisible(false);
    setModalConfig(null);
    setModalKey(undefined);
  }, []);

  const contextValue = useMemo(
    () => ({
      showModal,
      hideModal,
      isOpen: isVisible,
      modalKey
    }),
    [showModal, hideModal, isVisible, modalKey]
  );

  if (location.key !== prevLocationKey) {
    setPrevLocationKey(location.key);
    hideModal();
  }

  useEffect(() => {
    if (!isVisible) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      const shortcut = resolveModalShortcut(toModalKeyEvent(e), toModalFocusInfo(document.activeElement));
      if (!shortcut) return;

      if (shortcut.kind === "cancel") {
        hideModal();
        return;
      }

      const target = panelRef.current?.querySelector<HTMLButtonElement>(shortcutSelector(shortcut));
      if (!target || target.disabled) return;

      e.preventDefault();
      target.click();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isVisible, hideModal]);

  return (
    <GlobalModalContext.Provider value={contextValue}>
      {children}
      <ModalPortal isVisible={isVisible} modalConfig={modalConfig} hideModal={hideModal} panelRef={panelRef} />
    </GlobalModalContext.Provider>
  );
};

const ModalPortal = memo(
  ({
    isVisible,
    modalConfig,
    hideModal,
    panelRef,
  }: {
    isVisible: boolean;
    modalConfig: ModalConfig | null;
    hideModal: () => void;
    panelRef: RefObject<HTMLDivElement | null>;
  }) => {
    return createPortal(
      <div
        className={clsx(
          'fixed inset-0 flex items-center justify-center bg-black/60 z-50 transition-opacity duration-150 animate-fade-in',
          isVisible ? 'visible pointer-events-auto' : 'hidden pointer-events-none'
        )}
        onMouseDown={(e) => {
          if (e.target === e.currentTarget) hideModal();
        }}
      >
        {modalConfig && (
          <div
            ref={panelRef}
            className={clsx(
              'bg-theme-surface-2 border border-theme-border z-[51] rounded-xl compact:rounded-none compact:py-2 w-full shadow-xl transition duration-150 transform max-h-[calc(100dvh-4rem)] compact:max-h-dvh overflow-y-auto',
              modalConfig.tailwindMaxWidthClass || 'max-w-lg',
              modalConfig.isMaxHeight ? 'h-full flex flex-col sm:p-6 compact:sm:px-4' : 'p-6 compact:px-4'
            )}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className={modalConfig.isMaxHeight ? 'flex-1 min-h-0 flex flex-col' : 'mb-4 compact:mb-2'}>
              <RouteAreaContext.Provider value={modalConfig.openerArea}>
                {typeof modalConfig.content === 'function'
                  ? <modalConfig.content close={hideModal} />
                  : modalConfig.content}
              </RouteAreaContext.Provider>
            </div>
          </div>
        )}
      </div>,
      document.body
    );
  }
);
